import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react'
import { timeOf } from '../school_panel/lib/dates';

export interface ExamSession {
    id: string
    name: string
    status: 'setup' | 'extracting' | 'processing' | 'done' | 'error'
    step: number
    createdAt: number

    // Scope
    gradeId: string
    subjectId: string
    academicYearId: string
    selectedCourses: Array<{ id: string; name: string; academicYearId: string; academicYearName: string }>
    nationalExamMode: boolean
    // YADA Education Hub admins: school or university/college students (older sessions lack it: school)
    level?: 'school' | 'uni'
    // Hub university exam for one university (none: every university, under YADA Education Hub)
    universityId?: number | null
    universityName?: string
    selectedNationalExam: string
    tagId: string

    // Metadata
    formdata: { name: string; given_time: number; description: string }
    isNational: boolean

    // Exam
    createdExamId: string | null
    createdExamIds: string[] | null

    // Questions
    pendingQuestions: any[] | null
    questionsExtracted: boolean

    // Pipeline progress (for tab indicators)
    pipelinePhase: string | null
    pipelineTotal: number
    pipelineCompleted: number
    pipelineFailed: number
}

function createSession(name?: string): ExamSession {
    return {
        id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        name: name || `Exam ${timeOf(new Date())}`,
        status: 'setup',
        step: 1,
        createdAt: Date.now(),
        gradeId: '-1',
        subjectId: '-1',
        academicYearId: '-1',
        selectedCourses: [],
        nationalExamMode: false,
        selectedNationalExam: '-1',
        tagId: '-1',
        formdata: { name: '', given_time: 60, description: '' },
        isNational: false,
        createdExamId: null,
        createdExamIds: null,
        pendingQuestions: null,
        questionsExtracted: false,
        pipelinePhase: null,
        pipelineTotal: 0,
        pipelineCompleted: 0,
        pipelineFailed: 0,
    }
}

type Action =
    | { type: 'ADD_SESSION'; name?: string }
    | { type: 'REMOVE_SESSION'; id: string }
    | { type: 'SET_ACTIVE'; id: string }
    | { type: 'UPDATE_SESSION'; id: string; patch: Partial<ExamSession> }

interface State {
    sessions: ExamSession[]
    activeId: string | null
}

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case 'ADD_SESSION': {
            const s = createSession(action.name)
            return { sessions: [...state.sessions, s], activeId: s.id }
        }
        case 'REMOVE_SESSION': {
            const filtered = state.sessions.filter(s => s.id !== action.id)
            const newActive = state.activeId === action.id
                ? (filtered.length > 0 ? filtered[filtered.length - 1].id : null)
                : state.activeId
            return { sessions: filtered, activeId: newActive }
        }
        case 'SET_ACTIVE':
            return { ...state, activeId: action.id }
        case 'UPDATE_SESSION':
            return {
                ...state,
                sessions: state.sessions.map(s =>
                    s.id === action.id ? { ...s, ...action.patch } : s
                )
            }
        default:
            return state
    }
}

const STORAGE_KEY = 'exam_sessions_v2'

function loadState(): State {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (raw) {
            const parsed = JSON.parse(raw)
            if (parsed?.sessions?.length) return parsed
        }
    } catch { /* ignore */ }
    const initial = createSession()
    return { sessions: [initial], activeId: initial.id }
}

interface ContextValue {
    state: State
    activeSession: ExamSession | null
    addSession: (name?: string) => void
    removeSession: (id: string) => void
    setActive: (id: string) => void
    updateSession: (id: string, patch: Partial<ExamSession>) => void
    updateActive: (patch: Partial<ExamSession>) => void
}

const Ctx = createContext<ContextValue | null>(null)

export const ExamSessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, undefined, loadState)

    // Persist to localStorage
    useEffect(() => {
        try {
            // Don't persist pendingQuestions (too large)
            const slim = {
                ...state,
                sessions: state.sessions.map(s => ({ ...s, pendingQuestions: null, pipelinePhase: null, pipelineTotal: 0, pipelineCompleted: 0, pipelineFailed: 0 }))
            }
            localStorage.setItem(STORAGE_KEY, JSON.stringify(slim))
        } catch { /* ignore */ }
    }, [state])

    const addSession = useCallback((name?: string) => dispatch({ type: 'ADD_SESSION', name }), [])
    const removeSession = useCallback((id: string) => dispatch({ type: 'REMOVE_SESSION', id }), [])
    const setActive = useCallback((id: string) => dispatch({ type: 'SET_ACTIVE', id }), [])
    const updateSession = useCallback((id: string, patch: Partial<ExamSession>) => dispatch({ type: 'UPDATE_SESSION', id, patch }), [])
    const updateActive = useCallback((patch: Partial<ExamSession>) => {
        if (state.activeId) dispatch({ type: 'UPDATE_SESSION', id: state.activeId, patch })
    }, [state.activeId])

    const activeSession = state.sessions.find(s => s.id === state.activeId) || null

    return (
        <Ctx.Provider value={{ state, activeSession, addSession, removeSession, setActive, updateSession, updateActive }}>
            {children}
        </Ctx.Provider>
    )
}

export function useExamSessions() {
    const ctx = useContext(Ctx)
    if (!ctx) throw new Error('useExamSessions must be used within ExamSessionProvider')
    return ctx
}
